import { useEffect, useMemo, useRef, useState } from 'react';

import { Modal } from '../albums/Modal';
import {
  FORMATS,
  KINDS,
  ORIENTATIONS,
  SIZES,
  STYLES,
  THEMES,
  overlayUrl,
  type OverlayKind,
} from './overlay';
import { OverlayThumb } from './OverlayThumb';

/**
 * Stream graphics: pick one, copy the link, put it in OBS.
 *
 * ## Why a whole dialog for a URL
 *
 * The overlay has six appearance options and they all live in the query string,
 * because a browser source in OBS has no login and nowhere to store a
 * preference. Six query parameters is a thing a host cannot be asked to type,
 * so this builds the URL for them - and building it here rather than storing it
 * server-side is what lets one host run "now playing" in one scene and a queue
 * ticker in another without the two fighting.
 *
 * ## The preview is the real page
 *
 * It is an iframe of the same URL the copy button hands over, so what the host
 * approves is rendered by the code that will run inside OBS rather than by a
 * React drawing of it. The little pictures on the buttons are drawings; the
 * thing at the bottom is not.
 *
 * On a checkerboard, the way an image editor shows transparency. Previewing a
 * transparent overlay against this dialog's dark panel would show a card on a
 * dark surface, which is precisely what it will *not* look like over a camera
 * feed - and "why is there a black box behind it" is the support question this
 * exists to prevent.
 *
 * ## The URL is a secret and the dialog knows it
 *
 * The token is the only thing protecting it, and this is a screen hosts
 * screen-share while setting up. So the URL stays masked until asked for and
 * copying is the ordinary path: a host can hand it to OBS without ever putting
 * it on a stream.
 */
export function OverlayDialog({ token, onClose }: { token: string; onClose: () => void }) {
  const [kind, setKind] = useState<OverlayKind>('now');

  const [style, setStyle] = useState('glass');
  const [orientation, setOrientation] = useState('landscape');
  const [format, setFormat] = useState('card');
  const [theme, setTheme] = useState('dark');
  const [size, setSize] = useState('normal');
  const [rows, setRows] = useState(5);

  const [shown, setShown] = useState(false);
  const [copied, setCopied] = useState(false);
  const [preview, setPreview] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);

    return () => clearTimeout(timer);
  }, [copied]);

  /*
   * Scroll to the preview when it opens.
   *
   * The dialog body scrolls, and everything above the preview - three tabs,
   * two grids of choices, a slider and the URL - is taller than 70vh. Without
   * this, pressing Preview appends a panel below the fold and looks like a
   * button that does nothing.
   */
  useEffect(() => {
    if (!preview) return;

    previewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [preview]);

  const url = useMemo(
    () => overlayUrl(token, kind, { style, orientation, format, theme, size, rows }),
    [token, kind, style, orientation, format, theme, size, rows],
  );

  const list = kind !== 'now';

  return (
    <Modal title="Stream graphics" onClose={onClose} wide>
      <div className="flex flex-col gap-5" data-overlay-dialog>
        {/* ------------------------------------------------------- */}
        {/* Which graphic                                            */}
        {/* ------------------------------------------------------- */}
        <div
          role="tablist"
          aria-label="Overlay"
          className="flex gap-1 rounded-[var(--radius-control)] bg-app p-1"
        >
          {KINDS.map((option) => (
            <button
              key={option.value}
              type="button"
              role="tab"
              aria-selected={kind === option.value}
              data-overlay-kind={option.value}
              onClick={() => {
                setKind(option.value);
                // A preview of the graphic they just left is worse than none.
                setPreview(false);
              }}
              className={`flex-1 rounded-[calc(var(--radius-control)-2px)] px-3 py-2 text-sm font-medium transition-colors ${
                kind === option.value
                  ? 'bg-raised text-ink'
                  : 'text-soft hover:text-ink'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        <p className="text-[12px] leading-relaxed text-soft">
          {list
            ? `Add this as a Browser source in OBS, Streamlabs or TikTok Studio. It draws
               ${kind === 'queue' ? 'what is coming up' : 'what has already played'} on a
               transparent background, and updates itself.`
            : `Add this as a Browser source in OBS, Streamlabs or TikTok Studio. It draws the
               track that is playing on a transparent background, and updates itself.`}
        </p>

        {/* ------------------------------------------------------- */}
        {/* Appearance                                               */}
        {/* ------------------------------------------------------- */}
        {!list && (
          <>
            <Group label="Style">
              <div className="grid grid-cols-2 gap-2.5">
                {STYLES.map((option) => (
                  <Tile
                    key={option.value}
                    selected={style === option.value}
                    onSelect={() => setStyle(option.value)}
                    data-overlay-style={option.value}
                    label={option.label}
                    hint={option.hint}
                    thumb={
                      <OverlayThumb kind="now" style={option.value} orientation={orientation} />
                    }
                  />
                ))}
              </div>
            </Group>

            <Group label="Orientation">
              <Row>
                {ORIENTATIONS.map((option) => (
                  <Pill
                    key={option.value}
                    selected={orientation === option.value}
                    onSelect={() => setOrientation(option.value)}
                    data-overlay-orientation={option.value}
                  >
                    {option.label}
                  </Pill>
                ))}
              </Row>
            </Group>
          </>
        )}

        {list && (
          <>
            <Group label="Format">
              <div className="grid grid-cols-2 gap-2.5">
                {FORMATS.map((option) => (
                  <Tile
                    key={option.value}
                    selected={format === option.value}
                    onSelect={() => setFormat(option.value)}
                    data-overlay-format={option.value}
                    label={option.label}
                    hint={option.hint}
                  />
                ))}
              </div>
            </Group>

            <Group label="Theme">
              <div className="grid grid-cols-3 gap-2.5">
                {THEMES.map((option) => (
                  <Tile
                    key={option.value}
                    selected={theme === option.value}
                    onSelect={() => setTheme(option.value)}
                    data-overlay-theme={option.value}
                    label={option.label}
                    hint={option.hint}
                    thumb={<OverlayThumb kind="list" theme={option.value} format={format} />}
                  />
                ))}
              </div>
            </Group>

            <Group
              label="Text size"
              note="larger sizes help viewers watching the stream on a phone"
            >
              <Row>
                {SIZES.map((option) => (
                  <Pill
                    key={option.value}
                    selected={size === option.value}
                    onSelect={() => setSize(option.value)}
                    data-overlay-size={option.value}
                  >
                    {option.label}
                  </Pill>
                ))}
              </Row>
            </Group>

            <Group label="Rows">
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={1}
                  max={10}
                  step={1}
                  value={rows}
                  aria-label="Rows"
                  data-overlay-rows
                  onChange={(event) => setRows(Number(event.target.value))}
                  className="h-1.5 flex-1 cursor-pointer appearance-none rounded-full bg-raised accent-[var(--color-signal)]"
                />
                <span className="w-6 text-right font-mono text-sm tabular-nums text-ink">
                  {rows}
                </span>
              </div>
            </Group>
          </>
        )}

        {/* ------------------------------------------------------- */}
        {/* The link                                                 */}
        {/* ------------------------------------------------------- */}
        <div className="flex flex-col gap-2.5 border-t border-line pt-5">
          <div className="flex flex-wrap items-center gap-2">
            <code
              data-overlay-url
              className="min-w-0 flex-1 truncate rounded-[var(--radius-control)] border border-line bg-app px-2.5 py-2 font-mono text-[10px] text-soft"
            >
              {shown ? url : url.replace(token, '•'.repeat(12))}
            </code>

            <button
              type="button"
              data-overlay-reveal
              onClick={() => setShown((current) => !current)}
              className="meta-caps rounded-[var(--radius-pill)] border border-line px-2.5 py-2 text-soft transition-colors hover:border-line-strong hover:text-ink"
            >
              {shown ? 'Hide' : 'Show'}
            </button>
          </div>

          <p className="font-mono text-[10px] leading-relaxed text-ghost">
            treat this like a password — anyone with the link can see what your room is
            playing. the room has to be live for it to show anything.
          </p>

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button
              type="button"
              data-overlay-copy
              onClick={() => {
                /*
                 * A denied clipboard reveals the URL instead of doing nothing.
                 *
                 * Permission can be refused, and a page served over plain HTTP
                 * has no clipboard API at all. Swallowing that leaves a button
                 * that visibly does nothing, which reads as broken - showing
                 * the text so it can be selected by hand is the answer they
                 * came for anyway.
                 */
                navigator.clipboard
                  .writeText(url)
                  .then(() => setCopied(true))
                  .catch(() => setShown(true));
              }}
              className="flex flex-1 items-center justify-center gap-2 rounded-[var(--radius-control)] border border-transparent bg-accent px-4 py-2.5 text-sm font-semibold text-accent-ink transition-opacity hover:opacity-90"
            >
              <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" />
                <path d="M10.5 3.5v-1a1 1 0 0 0-1-1h-7a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h1" />
              </svg>
              {copied ? 'Copied' : 'Copy link'}
            </button>

            <button
              type="button"
              aria-pressed={preview}
              data-overlay-preview
              onClick={() => setPreview((current) => !current)}
              className="flex items-center justify-center gap-2 rounded-[var(--radius-control)] border border-line px-4 py-2.5 text-sm font-medium text-soft transition-colors hover:border-line-strong hover:text-ink"
            >
              <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                <path d="M1 8s2.6-4.5 7-4.5S15 8 15 8s-2.6 4.5-7 4.5S1 8 1 8Z" />
                <circle cx="8" cy="8" r="2" />
              </svg>
              {preview ? 'Hide preview' : 'Preview'}
            </button>
          </div>
        </div>

        {preview && (
          <div ref={previewRef} className="flex flex-col gap-2">
            <div className="flex items-baseline justify-between gap-3">
              <span className="meta-caps text-faint">live preview</span>

              {/* The same URL OBS will load, opened the way OBS loads it. */}
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                data-overlay-open
                className="meta-caps text-faint underline decoration-line underline-offset-4 transition-colors hover:text-soft"
              >
                open in a tab ↗
              </a>
            </div>

            <div
              data-overlay-frame
              className="overflow-hidden rounded-[var(--radius-control)] border border-line"
              style={{
                backgroundColor: '#2a2a3e',
                backgroundImage:
                  'linear-gradient(45deg,#20202f 25%,transparent 25%),linear-gradient(-45deg,#20202f 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#20202f 75%),linear-gradient(-45deg,transparent 75%,#20202f 75%)',
                backgroundSize: '16px 16px',
                backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0',
              }}
            >
              <iframe
                // Remounted when the options change, because the page reads them
                // once at load - the same as OBS reloading the browser source.
                key={url}
                src={url}
                title="Overlay preview"
                className="block w-full border-0"
                style={{ height: previewHeight(kind, { orientation, format, size, rows }) }}
                /*
                 * No `sandbox`.
                 *
                 * `allow-scripts` alone gives the frame an opaque origin, and
                 * the overlay's own poll of `/api/overlay/...` then counts as
                 * cross-origin and is refused - so the preview renders an empty
                 * card and teaches the host that their overlay is broken. This
                 * is a first-party page we serve; sandboxing it buys nothing.
                 */
              />
            </div>

            <p className="font-mono text-[10px] leading-relaxed text-ghost">
              the checkerboard is transparency — in OBS your camera or capture shows through it
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}

/**
 * Roughly what the graphic needs, so the frame is not mostly empty.
 *
 * An iframe cannot size itself to its content across origins and this one is
 * genuinely cross-origin in production, so the height is estimated from the
 * options rather than measured. Generous by design: a preview that clips the
 * bottom row teaches the host something false about the overlay.
 */
function previewHeight(
  kind: OverlayKind,
  options: { orientation: string; format: string; size: string; rows: number },
): number {
  const scale = { normal: 1, large: 1.28, xlarge: 1.6, xxlarge: 2 }[options.size] ?? 1;

  // The now-playing card has one row whatever the size control says - that
  // control belongs to the lists.
  if (kind === 'now') return options.orientation === 'portrait' ? 320 : 110;

  // A ticker is one line however many tracks are scrolling through it.
  if (options.format === 'ticker') return Math.round(78 * scale);

  return Math.round((44 + options.rows * 44) * scale);
}

/** A labelled block of choices. */
function Group({
  label,
  note,
  children,
}: {
  label: string;
  note?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2.5">
      <span className="meta-caps text-faint">
        {label}
        {note && <span className="ml-2 normal-case tracking-normal text-ghost">— {note}</span>}
      </span>
      {children}
    </div>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">{children}</div>;
}

/** One of a small set of words. */
function Pill({
  selected,
  onSelect,
  children,
  ...rest
}: {
  selected: boolean;
  onSelect: () => void;
  children: React.ReactNode;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={`rounded-[var(--radius-control)] border px-3 py-2.5 text-sm font-medium transition-colors ${
        selected
          ? 'border-fresh/60 bg-fresh/10 text-ink'
          : 'border-line text-soft hover:border-line-strong hover:text-ink'
      }`}
      {...rest}
    >
      {children}
    </button>
  );
}

/** A choice with a picture of what it does. */
function Tile({
  selected,
  onSelect,
  label,
  hint,
  thumb,
  ...rest
}: {
  selected: boolean;
  onSelect: () => void;
  label: string;
  hint: string;
  thumb?: React.ReactNode;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={`flex flex-col gap-2 rounded-[var(--radius-control)] border p-3 text-left transition-colors ${
        selected
          ? 'border-fresh/60 bg-fresh/[0.06]'
          : 'border-line hover:border-line-strong'
      }`}
      {...rest}
    >
      {thumb}
      <span className="text-sm font-semibold text-ink">{label}</span>
      <span className="text-[11px] leading-snug text-faint">{hint}</span>
    </button>
  );
}
